import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, XCircle, Clock, User, Calendar, Filter, RefreshCw, FileText, ChevronDown, Edit2, X, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const PaymentApprovals = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [processing, setProcessing] = useState({});
  
  // Edit modal state
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', payment_mode: '', reference: '', notes: '' });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    fetchCashiers();
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchSummary();
  }, [filter, cashierFilter]);

  const fetchCashiers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/cashiers-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCashiers(response.data);
    } catch (err) {
      console.error('Error fetching cashiers:', err);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      let url = `${API}/pending-payments?status=${filter}`;
      if (cashierFilter !== 'all') {
        url += `&cashier_id=${cashierFilter}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (err) {
      console.error('Error fetching payments:', err);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/payment-collection-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const handleApprove = async (paymentId) => {
    setProcessing(prev => ({ ...prev, [paymentId]: 'approve' }));
    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/pending-payments/${paymentId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment approved');
      fetchPayments();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setProcessing(prev => ({ ...prev, [paymentId]: null }));
    }
  };

  const handleReject = async (paymentId) => {
    const reason = prompt('Reason for rejection (optional):');
    setProcessing(prev => ({ ...prev, [paymentId]: 'reject' }));
    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/pending-payments/${paymentId}/reject?reason=${encodeURIComponent(reason || '')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment rejected');
      fetchPayments();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setProcessing(prev => ({ ...prev, [paymentId]: null }));
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setEditForm({
      amount: payment.amount,
      payment_mode: payment.payment_mode || 'cash',
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
  };

  const handleEditSave = async () => {
    if (!editingPayment) return;
    setEditSaving(true);
    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/pending-payments/${editingPayment.id}/edit`, {
        amount: parseFloat(editForm.amount),
        payment_mode: editForm.payment_mode,
        reference: editForm.reference,
        notes: editForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment updated');
      setEditingPayment(null);
      fetchPayments();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payment');
    } finally {
      setEditSaving(false);
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle size={12} /> Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1"><XCircle size={12} /> Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1"><Clock size={12} /> Pending</span>;
    }
  };

  const totalPending = summary.reduce((sum, s) => sum + s.pending_amount, 0);

  // Filter summary by selected cashier
  const filteredSummary = cashierFilter === 'all' 
    ? summary 
    : summary.filter(s => s.cashier_id === cashierFilter);

  return (
    <div data-testid="payment-approvals-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Payment Approvals</h1>
            <p className="text-slate-500">Review and approve cashier collections</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/gposp/payment-collection-history')}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700"
          >
            <FileText size={18} />
            Collection History
          </button>
          <button
            onClick={() => { fetchPayments(); fetchSummary(); }}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Total Pending Approval</p>
            <p className="text-2xl font-bold">{formatCurrency(totalPending)}</p>
          </div>
          {summary.filter(s => s.pending_count > 0).slice(0, 3).map(s => (
            <div 
              key={s.cashier_id} 
              className={`rounded-xl border p-4 cursor-pointer transition-all ${
                cashierFilter === s.cashier_id 
                  ? 'bg-violet-100 border-violet-500' 
                  : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
              onClick={() => setCashierFilter(cashierFilter === s.cashier_id ? 'all' : s.cashier_id)}
            >
              <p className="text-sm text-slate-500">{s.cashier_name}</p>
              <p className="text-lg font-bold text-amber-600">{formatCurrency(s.pending_amount)}</p>
              <p className="text-xs text-slate-400">{s.pending_count} pending</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Status Filter */}
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium capitalize ${
                filter === status
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        
        {/* Cashier Filter */}
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-slate-500" />
          <div className="relative">
            <select
              value={cashierFilter}
              onChange={(e) => setCashierFilter(e.target.value)}
              className="pl-3 pr-8 py-2 border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white min-w-[160px]"
            >
              <option value="all">All Cashiers</option>
              {cashiers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-violet-200 border-t-violet-600"></div>
        </div>
      ) : (
        /* Payments List */
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {payments.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="mx-auto mb-4 text-slate-300" size={48} />
              <p className="text-slate-500">No {filter} payments found</p>
              {cashierFilter !== 'all' && (
                <p className="text-sm text-slate-400 mt-1">
                  for selected cashier
                </p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {payments.map(payment => (
                <div key={payment.id} className="p-4 hover:bg-slate-50" data-testid={`payment-${payment.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                        <User className="text-violet-600" size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{payment.customer_name}</p>
                          {getStatusBadge(payment.status)}
                          {payment.original_amount && payment.original_amount !== payment.amount && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                              Edited (was ₹{payment.original_amount})
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{payment.customer_mobile}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            Collected: {formatDateTime(payment.created_at)}
                          </span>
                          <span className="font-medium text-slate-600">By: {payment.collected_by_name}</span>
                          <span className="capitalize">Mode: {payment.payment_mode}</span>
                        </div>
                        {payment.reference && (
                          <p className="text-xs text-slate-500 mt-1">Ref: {payment.reference}</p>
                        )}
                        {payment.notes && (
                          <p className="text-xs text-slate-400 mt-1">Note: {payment.notes}</p>
                        )}
                        {payment.rejected_reason && (
                          <p className="text-xs text-red-500 mt-1">Rejection reason: {payment.rejected_reason}</p>
                        )}
                        {payment.approved_at && (
                          <p className="text-xs text-green-600 mt-1">
                            {payment.status === 'approved' ? 'Approved' : 'Rejected'}: {formatDateTime(payment.approved_at)}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="text-xl font-bold text-violet-600">{formatCurrency(payment.amount)}</p>
                      
                      {payment.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(payment)}
                            className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-1"
                            title="Edit amount"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleApprove(payment.id)}
                            disabled={processing[payment.id]}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <CheckCircle size={16} />
                            {processing[payment.id] === 'approve' ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleReject(payment.id)}
                            disabled={processing[payment.id]}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <XCircle size={16} />
                            {processing[payment.id] === 'reject' ? 'Rejecting...' : 'Reject'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Edit Payment</h3>
              <button
                onClick={() => setEditingPayment(null)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Customer Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{editingPayment.customer_name}</p>
                <p className="text-sm text-slate-500">Collected by: {editingPayment.collected_by_name}</p>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                <input
                  type="number"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  step="0.01"
                  min="0"
                />
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                <select
                  value={editForm.payment_mode}
                  onChange={(e) => setEditForm({ ...editForm, payment_mode: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={editForm.reference}
                  onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="Transaction ID, receipt no., etc."
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setEditingPayment(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving || !editForm.amount}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2"
              >
                {editSaving ? 'Saving...' : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentApprovals;
